import { useEffect } from 'react';

export type LastPopulatedPageResolver = (requestedPage: number, abortSignal?: AbortSignal) => Promise<number>;

type PageState = {
  requestedPage: number;
  loading: boolean;
  objectsLength: number;
  hasMore?: boolean;
};

type UseLastPopulatedPageRedirectArgs = PageState & {
  enabled?: boolean;
  resolveLastPopulatedPage?: LastPopulatedPageResolver;
  onRedirect: (lastPopulatedPage: number, requestedPage: number) => void;
  onRedirectNotNeeded?: () => void;
  onFailure?: () => void;
};

export const shouldRedirectToLastPopulatedPage = ({
  requestedPage,
  loading,
  objectsLength,
  hasMore,
}: PageState): boolean => !loading && requestedPage > 1 && objectsLength === 0 && hasMore === false;

export const useLastPopulatedPageRedirect = ({
  requestedPage,
  loading,
  objectsLength,
  hasMore,
  enabled = true,
  resolveLastPopulatedPage,
  onRedirect,
  onRedirectNotNeeded,
  onFailure,
}: UseLastPopulatedPageRedirectArgs): void => {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const shouldRedirect = shouldRedirectToLastPopulatedPage({
      requestedPage,
      loading,
      objectsLength,
      hasMore,
    });

    if (!shouldRedirect || !resolveLastPopulatedPage) {
      onRedirectNotNeeded?.();

      return;
    }

    const abortController = new AbortController();

    void resolveLastPopulatedPage(requestedPage, abortController.signal)
      .then(lastPopulatedPage => {
        if (abortController.signal.aborted) {
          return;
        }

        onRedirect(lastPopulatedPage, requestedPage);
      })
      .catch(() => {
        if (abortController.signal.aborted) {
          return;
        }

        onFailure?.();
      });

    return () => {
      abortController.abort();
    };
  }, [
    enabled,
    hasMore,
    loading,
    objectsLength,
    onFailure,
    onRedirect,
    onRedirectNotNeeded,
    requestedPage,
    resolveLastPopulatedPage,
  ]);
};
